<template>
  <div class="card col-xl-12 col-lg-12">
    <div class="card-header d-flex align-items-center py-3">
      <div class="d-flex align-items-center">
        <a-button
          type="link"
          class="p-0 mr-3"
          @click="$router.push('/actuality')"
        >
          <a-icon
            type="arrow-left"
            style="color: black; font-size: 15px"
            class="d-flex align-items-center"
          />
        </a-button>
        <h5 class="mb-0">{{ $t("actualite.details") }}</h5>
      </div>
    </div>
    <div class="card-body">
      <!-- Files Gallery Section -->
      <div class="card p-4">
        <template v-if="loading">
          <a-skeleton :paragraph="{ rows: 6 }" active />
        </template>
        <template v-else>
          <div v-if="actuality.photos && actuality.photos.length > 0">
            <a-carousel arrows dots-class="slick-dots slick-thumb">
              <a slot="customPaging" slot-scope="props">
                <img
                  :src="getImgUrl(props.i)"
                  v-if="isImageFile(actuality.photos[props.i])"
                />
                <div v-else class="document-thumbnail">
                  <img
                    :src="getDocumentTypeImage(actuality.photos[props.i])"
                    class="document-thumbnail-icon"
                  />
                </div>
              </a>
              <div
                v-for="(file, index) in actuality.photos"
                :key="index"
                class="gallery-item"
              >
                <!-- Image files -->
                <img
                  v-if="isImageFile(file)"
                  :src="getFileUrl(file)"
                  class="gallery-image"
                  @click="previewFile(file)"
                />
                <!-- Document files -->
                <div v-else class="document-preview" @click="previewFile(file)">
                  <img
                    :src="getDocumentTypeImage(file)"
                    class="document-type-image"
                  />
                  <div class="document-name">{{ getFileName(file) }}</div>
                </div>
              </div>
            </a-carousel>
          </div>
          <div v-else class="empty-files">
            <a-empty :description="$t('actualite.noFiles')" />
          </div>
        </template>
      </div>

      <!-- Details Section -->
      <div class="card p-4">
        <template v-if="loading">
          <a-skeleton :paragraph="{ rows: 6 }" active />
        </template>
        <template v-else>
          <div
            v-if="actuality.videos && actuality.videos.length > 0"
            class="row mb-4"
          >
            <div class="col-md-3">
              <span>{{ $t("actualite.videos") || "Videos" }}</span>
            </div>
            <div class="col-md-9">
              <div
                v-for="(video, videoIndex) in actuality.videos"
                :key="video._id || videoIndex"
                class="video-item mb-3"
              >
                <VideoPlayer
                  :hls-url="getHlsUrl(video.hlsMasterKey)"
                  :thumbnail-url="getThumbnailUrl(video.thumbnailKey)"
                  :status="video.status"
                  :resources-url="settings.resourcesUrl"
                  :renditions="video.renditions || []"
                  :original-key="video.originalKey"
                  width="100%"
                  height="400px"
                />
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.titre") }}</span>
            </div>
            <div class="col-md-9">
              <span>{{ actuality.title }}</span>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.date") }}</span>
            </div>
            <div class="col-md-9">
              <span>{{
                actuality.plannifDate
                  ? moment(actuality.plannifDate).format("DD/MM/YYYY HH:mm:ss")
                  : "-"
              }}</span>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.categorie") }}</span>
            </div>
            <div class="col-md-9">
              <a-tag color="purple">{{ displayCategory(actuality.category) }}</a-tag>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.cibles") }}</span>
            </div>
            <div class="col-md-9">
              <a-tag
                v-for="target in actuality.target"
                :key="target"
                color="blue"
                class="mr-2"
              >
                {{ $t(`actualite.${getTargetTranslationKey(target)}`) }}
              </a-tag>
            </div>
          </div>

          <div
            class="row mb-4"
            v-if="
              (actuality.students && actuality.students.length > 0) ||
              (actuality.levels && actuality.levels.length > 0) ||
              (actuality.classrooms && actuality.classrooms.length > 0)
            "
          >
            <div class="col-md-3">
              <span>{{ $t("actualite.specificFor") }}</span>
            </div>
            <div class="col-md-9">
              <template
                v-if="actuality.students && actuality.students.length > 0"
              >
                <a-tag
                  v-for="student in actuality.students"
                  :key="student"
                  color="green"
                  class="mr-2"
                >
                  {{ getStudentFullname(student) }}
                </a-tag>
              </template>
              <template
                v-else-if="
                  actuality.classrooms && actuality.classrooms.length > 0
                "
              >
                <a-tag
                  v-for="classroom in actuality.classrooms"
                  :key="classroom"
                  color="green"
                  class="mr-2"
                >
                  {{ getClassroomName(classroom) }}
                </a-tag>
              </template>
              <template
                v-else-if="actuality.levels && actuality.levels.length > 0"
              >
                <a-tag
                  v-for="level in actuality.levels"
                  :key="level"
                  color="green"
                  class="mr-2"
                >
                  {{ $t(`niveau.niveau${level}`) }}
                </a-tag>
              </template>
            </div>
          </div>

          <div
            class="row mb-4"
            v-if="actuality.teachers && actuality.teachers.length > 0"
          >
            <div class="col-md-3">
              <span>{{ $t("actualite.teachers") }}</span>
            </div>
            <div class="col-md-9">
              <a-tag
                v-for="teacher in actuality.teachers"
                :key="teacher"
                color="orange"
                class="mr-2"
              >
                {{ getTeacherFullname(teacher) }}
              </a-tag>
            </div>
          </div>

          <!-- Creator -->
          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.createdByLabel") }}</span>
            </div>
            <div class="col-md-9">
              <div class="d-flex align-items-center">
                <template v-if="actuality.createdBy === 'teacher' && actuality.createdByTeacher">
                  <a-avatar
                    v-if="getCreatorPhoto()"
                    size="small"
                    :src="settings.resourcesUrl.replace('FILE_PATH', getCreatorPhoto())"
                    class="mr-2"
                  />
                  <a-avatar v-else size="small" icon="user" class="mr-2" />
                  <span>{{ getCreatorName() }}</span>
                  <a-tag color="cyan" class="ml-2">{{ $t("actualite.teacher") }}</a-tag>
                </template>
                <template v-else>
                  <a-avatar
                    v-if="logo"
                    size="small"
                    :src="logo"
                    class="mr-2"
                  />
                  <a-avatar v-else size="small" icon="user" class="mr-2" />
                  <span>{{ schoolName }}</span>
                  <a-tag color="geekblue" class="ml-2">{{ $t("actualite.admin") }}</a-tag>
                </template>
              </div>
            </div>
          </div>

          <!-- Validation Status -->
          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.statusLabel") }}</span>
            </div>
            <div class="col-md-9">
              <a-tag v-if="actuality.validationStatus === 'pending'" color="orange">
                {{ $t("actualite.pendingValidation") }}
              </a-tag>
              <a-tag v-else-if="actuality.validationStatus === 'rejected'" color="red">
                {{ $t("actualite.rejected") }}
              </a-tag>
              <a-tag v-else color="green">
                {{ $t("actualite.validatedLabel") }}
              </a-tag>
            </div>
          </div>

          <!-- Validation actions (only when pending) -->
          <div
            v-if="actuality.validationStatus === 'pending'"
            class="row mb-4 validation-actions-row"
          >
            <div class="col-md-3">
              <span>{{ $t("actualite.validation") }}</span>
            </div>
            <div class="col-md-9 d-flex align-items-center">
              <a-button
                type="primary"
                :loading="validating"
                class="mr-2"
                @click="confirmAction('approve')"
              >
                <a-icon type="check-circle" />
                {{ $t("actualite.approve") }}
              </a-button>
              <a-button
                danger
                :loading="validating"
                @click="confirmAction('reject')"
              >
                <a-icon type="close-circle" />
                {{ $t("actualite.reject") }}
              </a-button>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.mentionJaimes") }}</span>
            </div>
            <div class="col-md-9">
              <div class="d-flex align-items-center">
                <a-icon
                  type="heart"
                  theme="filled"
                  style="color: #ff4d4f; font-size: 18px"
                  class="mr-2"
                />
                <span
                  >{{ actuality.likes ? actuality.likes.length : 0 }}
                  {{ $t("actualite.jaimes") }}</span
                >
              </div>
            </div>
          </div>

          <div class="row mb-4">
            <div class="col-md-3">
              <span>{{ $t("actualite.seenNotSeenParentsTitle") }}</span>
            </div>
            <div class="col-md-9" v-if="!isParentTracked">
              <a-tooltip :title="$t('actualite.seenTrackedOnlyForParents')">
                <a-icon
                  type="eye-invisible"
                  style="font-size: 20px; color: rgba(0, 0, 0, 0.35); cursor: not-allowed"
                />
              </a-tooltip>
              <span style="padding-left: 8px; color: rgba(0, 0, 0, 0.55)">
                {{ $t("actualite.seenTrackedOnlyForParents") }}
              </span>
            </div>
            <div class="col-md-9" v-else>
              <a-skeleton :paragraph="{ rows: 3 }" active v-if="loadingSeenStatus" />
              <div v-else>
                <a-tabs :activeKey="activeSeenTab" @change="(key) => (activeSeenTab = key)">
                  <a-tab-pane key="seen" :tab="$t('actualite.seenCountLabel', { count: seenTotalCount })">
                    <div class="seen-status-table-scroll">
                      <table class="seen-status-table">
                        <thead>
                          <tr>
                            <th>{{ $t("actualite.parentLabel") }}</th>
                            <th>{{ $t("actualite.seenAtLabel") }}</th>
                          </tr>
                        </thead>
                        <tbody v-if="seenParents.length > 0">
                          <tr v-for="(parent, i) in seenParents" :key="'seen-' + i">
                            <td>
                              <span style="text-transform: capitalize">{{ formatParentLabel(parent) }}</span>
                              <span
                                v-if="formatStudentsLabel(parent)"
                                class="text-muted ml-2"
                                style="font-size: 12px; text-transform: capitalize"
                              >
                                {{ formatStudentsLabel(parent) }}
                              </span>
                            </td>
                            <td>{{ formatSeenDate(parent.seenAt) }}</td>
                          </tr>
                        </tbody>
                        <tbody v-else>
                          <tr>
                            <td colspan="2" class="text-muted">{{ $t("actualite.noParentSeenNews") }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-if="seenTotalPages > 1" class="seen-status-pagination">
                      <a-pagination
                        size="small"
                        :current="seenCurrentPage"
                        :total="seenTotalCount"
                        :pageSize="seenPageSize"
                        :showTotal="(total) => `${total}`"
                        @change="handleSeenPageChange"
                      />
                    </div>
                  </a-tab-pane>
                  <a-tab-pane key="notSeen" :tab="$t('actualite.notSeenCountLabel', { count: notSeenTotalCount })">
                    <div class="seen-status-table-scroll">
                      <table class="seen-status-table">
                        <thead>
                          <tr>
                            <th>{{ $t("actualite.parentLabel") }}</th>
                          </tr>
                        </thead>
                        <tbody v-if="notSeenParents.length > 0">
                          <tr v-for="(parent, i) in notSeenParents" :key="'not-seen-' + i">
                            <td>
                              <span style="text-transform: capitalize">{{ formatParentLabel(parent) }}</span>
                              <span
                                v-if="formatStudentsLabel(parent)"
                                class="text-muted ml-2"
                                style="font-size: 12px; text-transform: capitalize"
                              >
                                {{ formatStudentsLabel(parent) }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                        <tbody v-else>
                          <tr>
                            <td class="text-muted">{{ $t("actualite.allParentsSeenNews") }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-if="notSeenTotalPages > 1" class="seen-status-pagination">
                      <a-pagination
                        size="small"
                        :current="notSeenCurrentPage"
                        :total="notSeenTotalCount"
                        :pageSize="notSeenPageSize"
                        :showTotal="(total) => `${total}`"
                        @change="handleNotSeenPageChange"
                      />
                    </div>
                  </a-tab-pane>
                </a-tabs>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-3">
              <span>{{ $t("actualite.description") }}</span>
            </div>
            <div class="col-md-9">
              <div class="description-content" v-html="actuality.desc"></div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import { mapState } from "vuex";
import VideoPlayer from "@/components/VideoPlayer.vue";


export default {
  components: { VideoPlayer },
  computed: {
    ...mapState(["settings"]),
    isParentTracked() {
      return (
        Array.isArray(this.actuality?.target) &&
        this.actuality.target.some((t) => t === "parents" || t === "all")
      );
    },
    activeBuildingImage() {
      const images = this.settings?.image;
      if (!Array.isArray(images) || !images.length) return null;
      return (
        images.find((img) => img.db === this.settings.activeBuilding) ||
        images[0] ||
        null
      );
    },
    logo() {
      return this.activeBuildingImage?.logo || null;
    },
    schoolName() {
      return this.activeBuildingImage?.name || "";
    },
  },

  data: () => ({
    actuality: {},
    classes: [],
    studentList: [],
    teacherList: [],
    seenParents: [],
    notSeenParents: [],
    activeSeenTab: "seen",
    loadingSeenStatus: false,
    loading: true,
    validating: false,
    seenCurrentPage: 1,
    seenPageSize: 50,
    seenTotalCount: 0,
    seenTotalPages: 1,
    notSeenCurrentPage: 1,
    notSeenPageSize: 50,
    notSeenTotalCount: 0,
    notSeenTotalPages: 1,
  }),

  async created() {
    const found = await this.getActuality();
    if (!found) return;
    if (this.isParentTracked) {
      await this.fetchSeenStatus();
    }

    apiClient
      .post("/students/filter", {
        query: {
          status: "active",
        },
      })
      .then(({ data }) => {
        this.studentList = data;
      });

    apiClient
      .post("/classrooms/filter", {
        query: {
          status: "active",
        },
        aggregation: {
          $project: {
            _id: 1,
            name: 1,
          },
        },
      })
      .then((res) => (this.classes = res.data));

    apiClient
      .post("/teachers/filter", {
        query: {
          status: "active",
        },
        aggregation: {
          $project: {
            _id: 1,
            employee: 1,
          },
        },
      })
      .then(({ data }) => {
        this.teacherList = data;
      });

    this.loading = false;
  },

  methods: {
    moment,
    getStudentFullname(id) {
      const student = this.studentList.find((s) => s._id == id);
      if (student) return student.firstName + " " + student.lastName;
    },
    getClassroomName(id) {
      const classe = this.classes.find((s) => s._id == id);
      if (classe) return classe.name;
    },
    getTeacherFullname(id) {
      const teacher = this.teacherList.find((t) => t._id == id);
      if (teacher && teacher.employee) {
        return teacher.employee.firstName + " " + teacher.employee.lastName;
      }
      return "";
    },
    getCreatorName() {
      if (this.actuality.createdBy === "teacher" && this.actuality.createdByTeacher) {
        const teacher = this.teacherList.find(
          (t) => t._id == this.actuality.createdByTeacher
        );
        if (teacher && teacher.employee) {
          return teacher.employee.firstName + " " + teacher.employee.lastName;
        }
      }
      return this.schoolName;
    },
    getCreatorPhoto() {
      if (this.actuality.createdBy === "teacher" && this.actuality.createdByTeacher) {
        const teacher = this.teacherList.find(
          (t) => t._id == this.actuality.createdByTeacher
        );
        if (teacher && teacher.employee && teacher.employee.photo) {
          return teacher.employee.photo;
        }
      }
      return null;
    },
    async getActuality() {
      try {
        const { data } = await apiClient.get(
          "/actuality/details/" + this.$route.params.id
        );
        this.actuality = data || {};
        return !!data;
      } catch (e) {
        this.actuality = {};
        this.$message.error(this.$t("error.aucActualite"));
        this.$router.push("/actuality");
        return false;
      }
    },
    isImageFile(file) {
      const ext = file.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png"].includes(ext);
    },
    getDocumentTypeImage(file) {
      const ext = file.split(".").pop().toLowerCase();
      const imageMap = {
        pdf: "/resources/images/pdf-icon.png",
        doc: "/resources/images/word-icon.png",
        docx: "/resources/images/word-icon.png",
      };
      return imageMap[ext];
    },
    getFileName(file) {
      const fullName = file.split("/").pop();

      // Find the position of the timestamp separator
      const separatorIndex = fullName.indexOf("--");
      const nameAfterTimestamp =
        separatorIndex !== -1
          ? fullName.substring(separatorIndex + 2)
          : fullName;

      return nameAfterTimestamp;
    },
    getFileUrl(file) {
      return this.settings.resourcesUrl.replace("FILE_PATH", file);
    },
    getHlsUrl(hlsMasterKey) {
      if (!hlsMasterKey) return null;
      return this.settings.resourcesUrl.replace("FILE_PATH", hlsMasterKey);
    },
    getThumbnailUrl(thumbnailKey) {
      if (!thumbnailKey) return null;
      return this.settings.resourcesUrl.replace("FILE_PATH", thumbnailKey);
    },
    previewFile(file) {
      if (this.isImageFile(file)) {
        // Image preview logic
        window.open(this.getFileUrl(file), "_blank");
      } else {
        // Document download
        window.open(this.getFileUrl(file), "_blank");
      }
    },
    getImgUrl(i) {
      return this.settings.resourcesUrl.replace(
        "FILE_PATH",
        this.actuality.photos[i]
      );
    },
    getTargetTranslationKey(target) {
      const keyMap = {
        all: "tous",
        teacher: "enseignants",
        students: "eleves",
        administration: "administration",
        parents: "parents",
      };
      return keyMap[target] || target;
    },
    displayCategory(category) {
      if (category === "album") return this.$t("actualite.album");
      const categoryMap = {
        info: this.$t("actualite.categoryInfo"),
        important_info: this.$t("actualite.categoryImportantInfo"),
        urgent: this.$t("actualite.categoryUrgent"),
        avis_important: this.$t("actualite.categoryAvisImportant"),
        avis: this.$t("actualite.categoryAvis"),
        important: this.$t("actualite.categoryImportant"),
      };
      return categoryMap[category] || category;
    },
    formatSeenDate(date) {
      if (!date) return "-";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    formatParentLabel(parent) {
      if (!parent) return "";
      return parent.parentType
        ? `${parent.parentName} (${parent.parentType})`
        : parent.parentName;
    },
    formatStudentsLabel(parent) {
      if (!parent || !Array.isArray(parent.students) || parent.students.length === 0) return "";
      const names = parent.students
        .map((s) => `${s.firstName || ""} ${s.lastName || ""}`.trim())
        .filter(Boolean);
      return names.length > 0 ? `(${names.join(", ")})` : "";
    },
    async _fetchSeenPage({ seenPage, notSeenPage } = {}) {
      const id = this.$route.params.id;
      if (!id) return;
      const params = {
        seenPage: seenPage || this.seenCurrentPage,
        seenLimit: this.seenPageSize,
        notSeenPage: notSeenPage || this.notSeenCurrentPage,
        notSeenLimit: this.notSeenPageSize,
      };
      const { data } = await apiClient.get(`/actuality/${id}/seen-status`, { params });
      this.seenTotalCount = data.seenCount || 0;
      this.notSeenTotalCount = data.notSeenCount || 0;
      this.seenTotalPages = (data.pagination && data.pagination.seenTotalPages) || 1;
      this.notSeenTotalPages = (data.pagination && data.pagination.notSeenTotalPages) || 1;
      this.seenParents = data.seenParents || [];
      this.notSeenParents = data.notSeenParents || [];
    },
    async _fetchWithRetry(fn, retries = 2, delayMs = 1500) {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await fn();
        } catch (err) {
          if (attempt === retries) throw err;
          await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
        }
      }
    },
    async fetchSeenStatus() {
      if (!this.$route.params.id) return;
      this.loadingSeenStatus = true;
      this.activeSeenTab = "seen";
      this.seenCurrentPage = 1;
      this.notSeenCurrentPage = 1;
      try {
        await this._fetchWithRetry(() => this._fetchSeenPage());
      } catch (e) {
        this.seenParents = [];
        this.notSeenParents = [];
      } finally {
        this.loadingSeenStatus = false;
      }
    },
    async handleSeenPageChange(page) {
      this.seenCurrentPage = page;
      this.loadingSeenStatus = true;
      try {
        await this._fetchWithRetry(() => this._fetchSeenPage({ seenPage: page }));
      } catch (e) {
        // keep current data on error
      } finally {
        this.loadingSeenStatus = false;
      }
    },
    async handleNotSeenPageChange(page) {
      this.notSeenCurrentPage = page;
      this.loadingSeenStatus = true;
      try {
        await this._fetchWithRetry(() => this._fetchSeenPage({ notSeenPage: page }));
      } catch (e) {
        // keep current data on error
      } finally {
        this.loadingSeenStatus = false;
      }
    },
    confirmAction(action) {
      const id = this.$route.params.id;
      if (!id) return;
      const titleMap = {
        approve: this.$t("actualite.approve") + "?",
        reject: this.$t("actualite.confirmReject"),
      };
      this.$confirm({
        title: titleMap[action],
        okText: this.$t("all.okay"),
        cancelText: this.$t("all.cancel"),
        onOk: () => {
          if (action === "approve") return this.validateNews(id);
          if (action === "reject") return this.rejectNews(id);
        },
      });
    },
    async validateNews(id) {
      this.validating = true;
      try {
        await apiClient.patch("/actuality/" + id + "/validation-status", {
          validationStatus: "validated",
        });
        this.$message.success(this.$t("actualite.newsValidated"));
        await this.getActuality();
      } catch (error) {
        this.$message.error(this.$t("error.unexpectedError"));
      } finally {
        this.validating = false;
      }
    },
    async rejectNews(id) {
      this.validating = true;
      try {
        await apiClient.patch("/actuality/" + id + "/validation-status", {
          validationStatus: "rejected",
        });
        this.$message.success(this.$t("actualite.newsRejected"));
        await this.getActuality();
      } catch (error) {
        this.$message.error(this.$t("error.unexpectedError"));
      } finally {
        this.validating = false;
      }
    },
  },
};
</script>

<style scoped>
/* For demo */
.ant-carousel >>> .slick-dots {
  height: auto;
  position: relative;
  bottom: 0;
  margin: 10px 0;
}

.ant-carousel >>> .slick-slide img {
  border: 5px solid #fff;
  display: block;
  margin: auto;
  max-width: 80%;
}

.ant-carousel >>> .slick-thumb {
  bottom: 0;
  padding: 0;
}

.ant-carousel >>> .slick-thumb li {
  width: 80px;
  height: 70px;
  margin: 0 4px;
}

.ant-carousel >>> .slick-thumb li img {
  width: 100%;
  height: 100%;
  filter: grayscale(100%);
  border-radius: 4px;
}

.ant-carousel >>> .slick-thumb li.slick-active img {
  filter: grayscale(0%);
}

.ql-editor {
  height: 100px;
}

.minimized-image {
  height: 50px;
  width: 50px;
  object-fit: contain;
}

.gallery-item {
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 20px;
}

.gallery-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 15px;
}

.document-preview {
  text-align: center;
  cursor: pointer;
  padding: 20px;
  border: 1px solid #e8e8e8;
  width: 350px;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(194, 194, 194, 0.1);
}

.document-type-image {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.document-name {
  font-size: 16px;
  color: #333;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.document-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}

.document-thumbnail-icon {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

.empty-files {
  padding: 40px;
  text-align: center;
  background: #fafafa;
  border-radius: 8px;
}

.gallery-skeleton,
.gallery-skeleton-main,
.gallery-skeleton-thumbs,
.skeleton-tag {
  display: none;
}

.validation-actions-row {
  padding: 12px 0;
  border-top: 1px solid #f0f0f0;
}

.seen-status-table {
  width: 100%;
  border-collapse: collapse;
}

.seen-status-table th,
.seen-status-table td {
  border: 1px solid #f0f0f0;
  padding: 8px 10px;
  text-align: left;
}

.seen-status-table-scroll {
  max-height: 320px;
  overflow-y: auto;
}

.seen-status-pagination {
  display: flex;
  justify-content: center;
  padding: 10px 0 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
}
</style>
